import { useState, useCallback } from 'react';
import {
  ChevronRight, ChevronDown, Search, RefreshCw,
  BookOpen, Layers, CircleDot,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { Spinner } from '@/components/ui/Spinner';
import type { AccountTreeNodeDto, AccountType } from '@/types/account.types';

// ── Type colour palette ────────────────────────────────────────────────────────

const TYPE_COLOUR: Record<AccountType, { bg: string; text: string; border: string; dot: string }> = {
  Asset:     { bg: 'bg-blue-50 dark:bg-blue-900/20',     text: 'text-blue-700 dark:text-blue-300',     border: 'border-blue-200 dark:border-blue-700',   dot: 'bg-blue-500' },
  Liability: { bg: 'bg-orange-50 dark:bg-orange-900/20', text: 'text-orange-700 dark:text-orange-300', border: 'border-orange-200 dark:border-orange-700', dot: 'bg-orange-500' },
  Equity:    { bg: 'bg-purple-50 dark:bg-purple-900/20', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-200 dark:border-purple-700', dot: 'bg-purple-500' },
  Revenue:   { bg: 'bg-emerald-50 dark:bg-emerald-900/20', text: 'text-emerald-700 dark:text-emerald-300', border: 'border-emerald-200 dark:border-emerald-700', dot: 'bg-emerald-500' },
  Expense:   { bg: 'bg-rose-50 dark:bg-rose-900/20',     text: 'text-rose-700 dark:text-rose-300',     border: 'border-rose-200 dark:border-rose-700',   dot: 'bg-rose-500' },
};

// ── Tree Node ─────────────────────────────────────────────────────────────────

interface TreeNodeProps {
  node:        AccountTreeNodeDto;
  depth:       number;
  selected:    string | null;
  expanded:    Set<string>;
  searchQuery: string;
  onSelect:    (node: AccountTreeNodeDto) => void;
  onToggle:    (id: string) => void;
}

function matchesSearch(node: AccountTreeNodeDto, q: string): boolean {
  if (!q) return true;
  const lower = q.toLowerCase();
  return (
    node.code.toLowerCase().includes(lower) ||
    node.name.toLowerCase().includes(lower) ||
    (node.accountCategory?.toLowerCase().includes(lower) ?? false)
  );
}

function subtreeHasMatch(node: AccountTreeNodeDto, q: string): boolean {
  if (!q) return true;
  if (matchesSearch(node, q)) return true;
  return node.children.some(c => subtreeHasMatch(c, q));
}

const TreeNode = ({
  node, depth, selected, expanded, searchQuery, onSelect, onToggle,
}: TreeNodeProps) => {
  const isExpanded  = expanded.has(node.id);
  const isSelected  = selected === node.id;
  const hasChildren = node.children.length > 0;
  const colours     = TYPE_COLOUR[node.accountType];

  // Filter children by search
  const visibleChildren = searchQuery
    ? node.children.filter(c => subtreeHasMatch(c, searchQuery))
    : node.children;

  const isVisible = searchQuery ? subtreeHasMatch(node, searchQuery) : true;
  if (!isVisible) return null;

  // Auto-expand when search is active
  const showChildren = hasChildren && (searchQuery ? true : isExpanded);

  return (
    <div>
      <button
        type="button"
        onClick={() => onSelect(node)}
        onDoubleClick={() => hasChildren && onToggle(node.id)}
        className={cn(
          'w-full flex items-center gap-1 text-left group transition-colors',
          'hover:bg-gray-100 dark:hover:bg-gray-700/60',
          isSelected && 'bg-brand-50 dark:bg-brand-900/30 hover:bg-brand-50 dark:hover:bg-brand-900/30',
          !node.isActive && 'opacity-50',
        )}
        style={{ paddingLeft: `${depth * 16 + 4}px`, paddingTop: '3px', paddingBottom: '3px', paddingRight: '8px' }}
        title={`${node.code} — ${node.name}`}
      >
        {/* Expand/collapse toggle */}
        <span
          className={cn(
            'flex-shrink-0 w-4 h-4 flex items-center justify-center rounded',
            'text-gray-400 group-hover:text-gray-600',
            hasChildren && 'hover:bg-gray-200 dark:hover:bg-gray-600 cursor-pointer',
          )}
          onClick={e => { e.stopPropagation(); if (hasChildren) onToggle(node.id); }}
        >
          {hasChildren
            ? (isExpanded || searchQuery
                ? <ChevronDown size={12} />
                : <ChevronRight size={12} />)
            : <span className="w-3 h-3" />}
        </span>

        {/* Account icon */}
        <span className="flex-shrink-0">
          {!node.isPostingAccount
            ? <Layers size={12} className={cn('opacity-60', colours.text)} />
            : node.isControlAccount
              ? <BookOpen size={12} className={cn('opacity-60', colours.text)} />
              : <CircleDot size={12} className={cn('opacity-40', colours.text)} />}
        </span>

        {/* Code */}
        <span className={cn(
          'flex-shrink-0 font-mono text-[10px] font-semibold w-16 truncate',
          isSelected ? 'text-brand-700 dark:text-brand-300' : 'text-gray-500 dark:text-gray-400',
        )}>
          {node.code}
        </span>

        {/* Name */}
        <span className={cn(
          'flex-1 text-xs truncate',
          isSelected
            ? 'text-brand-800 dark:text-brand-200 font-medium'
            : 'text-gray-700 dark:text-gray-300',
          !node.isPostingAccount && 'font-medium',
        )}>
          {node.name}
        </span>

        {/* Badges */}
        {node.isControlAccount && (
          <span className="flex-shrink-0 text-[9px] px-1 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 font-semibold">
            CTRL
          </span>
        )}
        {!node.isActive && (
          <span className="flex-shrink-0 text-[9px] px-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-400 font-semibold">
            OFF
          </span>
        )}
      </button>

      {/* Children */}
      {showChildren && visibleChildren.length > 0 && (
        <div>
          {visibleChildren.map(child => (
            <TreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              selected={selected}
              expanded={expanded}
              searchQuery={searchQuery}
              onSelect={onSelect}
              onToggle={onToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Root type group separator ─────────────────────────────────────────────────

const TYPE_LABELS: Record<AccountType, string> = {
  Asset:     'Assets',
  Liability: 'Liabilities',
  Equity:    'Equity',
  Revenue:   'Revenue',
  Expense:   'Expenses',
};

// ── Main component ────────────────────────────────────────────────────────────

export interface AccountTreePanelProps {
  nodes:      AccountTreeNodeDto[];
  loading:    boolean;
  selectedId: string | null;
  onSelect:   (node: AccountTreeNodeDto) => void;
  onRefresh:  () => void;
}

export const AccountTreePanel = ({
  nodes, loading, selectedId, onSelect, onRefresh,
}: AccountTreePanelProps) => {
  const [expanded,     setExpanded]     = useState<Set<string>>(new Set());
  const [searchQuery,  setSearchQuery]  = useState('');
  const [expandedTypes, setExpandedTypes] = useState<Set<AccountType>>(
    new Set(['Asset', 'Liability', 'Equity', 'Revenue', 'Expense'])
  );

  const toggleNode = useCallback((id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleType = (type: AccountType) => {
    setExpandedTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type); else next.add(type);
      return next;
    });
  };

  const expandAll = () => {
    const allIds = new Set<string>();
    const collect = (n: AccountTreeNodeDto) => { allIds.add(n.id); n.children.forEach(collect); };
    nodes.forEach(collect);
    setExpanded(allIds);
    setExpandedTypes(new Set(['Asset', 'Liability', 'Equity', 'Revenue', 'Expense']));
  };

  const collapseAll = () => {
    setExpanded(new Set());
  };

  // Group root nodes by account type (order: Asset → Liability → Equity → Revenue → Expense)
  const typeOrder: AccountType[] = ['Asset', 'Liability', 'Equity', 'Revenue', 'Expense'];
  const byType = typeOrder.reduce<Record<AccountType, AccountTreeNodeDto[]>>(
    (acc, t) => ({ ...acc, [t]: [] }),
    {} as Record<AccountType, AccountTreeNodeDto[]>,
  );
  nodes.forEach(n => { (byType[n.accountType] ??= []).push(n); });

  const totalAccounts = nodes.reduce<number>((sum, n) => {
    const countTree = (node: AccountTreeNodeDto): number =>
      1 + node.children.reduce((s, c) => s + countTree(c), 0);
    return sum + countTree(n);
  }, 0);

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700">

      {/* Panel header */}
      <div className="flex-shrink-0 px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
            Chart of Accounts
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={expandAll}
              title="Expand all"
              className="text-[10px] text-gray-400 hover:text-brand-600 px-1"
            >
              Expand
            </button>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            <button
              type="button"
              onClick={collapseAll}
              title="Collapse all"
              className="text-[10px] text-gray-400 hover:text-brand-600 px-1"
            >
              Collapse
            </button>
            <button
              type="button"
              onClick={onRefresh}
              disabled={loading}
              title="Refresh tree"
              className="ml-1 w-5 h-5 flex items-center justify-center rounded text-gray-400 hover:text-brand-600 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              {loading ? <Spinner size="sm" /> : <RefreshCw size={11} />}
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search accounts…"
            className={cn(
              'w-full h-7 pl-6 pr-2 rounded border text-xs',
              'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100',
              'border-gray-300 dark:border-gray-600',
              'placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-brand-500',
            )}
          />
        </div>

        <div className="mt-1 text-[10px] text-gray-400">
          {totalAccounts} account{totalAccounts !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Tree body */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden select-none">
        {loading && (
          <div className="flex items-center justify-center h-20">
            <Spinner size="sm" />
          </div>
        )}

        {!loading && nodes.length === 0 && (
          <div className="px-4 py-8 text-center text-xs text-gray-400">
            No accounts found.<br />
            <span className="text-gray-300">Use the form to create the first account.</span>
          </div>
        )}

        {!loading && typeOrder.map(type => {
          const typeNodes = byType[type];
          if (typeNodes.length === 0 && !searchQuery) return null;

          const colours = TYPE_COLOUR[type];
          const isTypeExpanded = expandedTypes.has(type);
          const visibleNodes = searchQuery
            ? typeNodes.filter(n => subtreeHasMatch(n, searchQuery))
            : typeNodes;

          if (visibleNodes.length === 0) return null;

          return (
            <div key={type} className="border-b border-gray-100 dark:border-gray-700/50 last:border-0">
              {/* Type group header */}
              <button
                type="button"
                onClick={() => toggleType(type)}
                className={cn(
                  'w-full flex items-center gap-2 px-2 py-1.5',
                  'text-left sticky top-0 z-10',
                  'border-b border-gray-200 dark:border-gray-700',
                  colours.bg,
                )}
              >
                <span className={cn('w-2 h-2 rounded-full flex-shrink-0', colours.dot)} />
                <span className={cn('text-[11px] font-bold tracking-wide flex-1', colours.text)}>
                  {TYPE_LABELS[type]}
                </span>
                <span className="text-[10px] text-gray-400 mr-1">
                  {visibleNodes.reduce<number>((s, n) => {
                    const count = (node: AccountTreeNodeDto): number =>
                      1 + node.children.reduce((ss, c) => ss + count(c), 0);
                    return s + count(n);
                  }, 0)}
                </span>
                {isTypeExpanded
                  ? <ChevronDown size={11} className={colours.text} />
                  : <ChevronRight size={11} className={colours.text} />}
              </button>

              {/* Type nodes */}
              {isTypeExpanded && (
                <div className="py-0.5">
                  {visibleNodes.map(node => (
                    <TreeNode
                      key={node.id}
                      node={node}
                      depth={0}
                      selected={selectedId}
                      expanded={expanded}
                      searchQuery={searchQuery}
                      onSelect={onSelect}
                      onToggle={toggleNode}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
